import React, { useState } from 'react';
import { PlusCircle, Trash2, CalendarDays, Clock } from 'lucide-react';

const EMPTY_SHIFT = { date: '', startTime: '', endTime: '', staffsRequired: 1 };

function fmtLocal(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const Step2Schedule = ({ schedule, onUpdate }) => {
  const { serviceType, hourlyPayRate, location, formattedAddress, shifts } = schedule;

  const update = (field, value) => onUpdate({ ...schedule, [field]: value });

  const addShift = () => onUpdate({ ...schedule, shifts: [...shifts, { ...EMPTY_SHIFT }] });

  const removeShift = (i) =>
    onUpdate({ ...schedule, shifts: shifts.filter((_, idx) => idx !== i) });

  const editShift = (i, field, value) => {
    const updated = shifts.map((s, idx) =>
      idx === i ? { ...s, [field]: value } : s
    );
    onUpdate({ ...schedule, shifts: updated });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Set the job details and add one or more shifts.
      </p>

      {/* Job details */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-foreground">Service Type</label>
          <input
            value={serviceType}
            onChange={(e) => update('serviceType', e.target.value)}
            placeholder="e.g. Security Guard"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-pink/30"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-foreground">Hourly Pay Rate ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={hourlyPayRate}
            onChange={(e) => update('hourlyPayRate', e.target.value)}
            placeholder="25.00"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-pink/30"
          />
        </div>
        <div className="col-span-2">
          <label className="mb-1.5 block text-xs font-semibold text-foreground">Location / Address</label>
          <input
            value={formattedAddress}
            onChange={(e) => { update('formattedAddress', e.target.value); update('location', e.target.value); }}
            placeholder="123 Main St, City, State"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-pink/30"
          />
        </div>
      </div>

      {/* Shifts */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold text-foreground">Shifts</p>
          <button
            onClick={addShift}
            className="flex items-center gap-1.5 rounded-lg bg-brand-pink/10 px-3 py-1.5 text-xs font-medium text-brand-pink transition-colors hover:bg-brand-pink/20"
          >
            <PlusCircle size={12} />
            Add Shift
          </button>
        </div>

        {shifts.length === 0 && (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-border py-8 text-sm text-muted-foreground">
            <CalendarDays size={24} className="mb-2 text-muted-foreground/40" />
            No shifts added yet. Click "Add Shift" to start.
          </div>
        )}

        <div className="space-y-2">
          {shifts.map((s, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-3">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {i + 1}
              </div>
              <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Date</label>
                  <input
                    type="date"
                    value={s.date}
                    onChange={(e) => editShift(i, 'date', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-pink/30"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Start Time</label>
                  <input
                    type="time"
                    value={s.startTime}
                    onChange={(e) => editShift(i, 'startTime', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-pink/30"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">End Time</label>
                  <input
                    type="time"
                    value={s.endTime}
                    onChange={(e) => editShift(i, 'endTime', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-pink/30"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Staff Needed</label>
                  <input
                    type="number"
                    min="1"
                    value={s.staffsRequired}
                    onChange={(e) => editShift(i, 'staffsRequired', Number(e.target.value))}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-pink/30"
                  />
                </div>
              </div>
              <button
                onClick={() => removeShift(i)}
                className="flex-shrink-0 text-muted-foreground transition-colors hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {shifts.length > 0 && (
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Shifts Preview
          </p>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-1 pr-3 text-left font-semibold text-muted-foreground">#</th>
                  <th className="pb-1 pr-3 text-left font-semibold text-muted-foreground">Date</th>
                  <th className="pb-1 pr-3 text-left font-semibold text-muted-foreground">Time</th>
                  <th className="pb-1 text-left font-semibold text-muted-foreground">Staff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shifts.map((s, i) => (
                  <tr key={i}>
                    <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-1 pr-3">{s.date ? fmtLocal(s.date) : '—'}</td>
                    <td className="py-1 pr-3">
                      <div className="flex items-center gap-1">
                        <Clock size={10} className="text-muted-foreground" />
                        {s.startTime || '?'} – {s.endTime || '?'}
                      </div>
                    </td>
                    <td className="py-1">{s.staffsRequired}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2Schedule;
